import { Button } from '@bpinternal/ui-kit-next'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { Boundary } from '~/components'
import { Spinner } from '~/elements'
import { usePlanProducts } from '~/features/billing/hooks/usePlanProducts'
import { usePurchasePlan } from '~/features/billing/hooks/usePurchasePlan'

const checkoutTriggerSearchSchema = z.object({
  plan: z
    .enum(['plus', 'team'])
    .optional()
    .catch(() => undefined),
  cycle: z
    .enum(['monthly', 'annual'])
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/checkout-trigger')({
  validateSearch: checkoutTriggerSearchSchema,
  component: () => (
    <Boundary>
      <CheckoutTriggerComponent />
    </Boundary>
  ),
})

function CheckoutTriggerComponent() {
  const { workspaceId } = Route.useParams()
  const { plan, cycle } = Route.useSearch()
  const navigate = useNavigate()
  const { currentPlan, planProducts } = usePlanProducts(workspaceId)
  const { mutate: purchasePlan, isError, error } = usePurchasePlan(workspaceId)
  const triggered = useRef(false)

  useEffect(() => {
    if (triggered.current) return
    triggered.current = true

    if (!plan || !cycle) {
      void navigate({ to: '/workspaces/$workspaceId/billing', params: { workspaceId }, replace: true })
      return
    }

    const requestedCycle = cycle === 'annual' ? 'yearly' : 'monthly'

    // Guard: already on this plan — no re-purchase needed
    if (currentPlan.legacyName === plan && currentPlan.cycle === requestedCycle) {
      void navigate({ to: '/workspaces/$workspaceId/billing', params: { workspaceId }, replace: true })
      return
    }

    const desiredPlan = planProducts.find((p) => p.legacyName === plan && p.cycle === requestedCycle)

    if (!desiredPlan) {
      // Plan not found or invalid — go to billing
      void navigate({ to: '/workspaces/$workspaceId/billing', params: { workspaceId }, replace: true })
      return
    }

    purchasePlan({ desiredPlan, isYearly: cycle === 'annual' })
  }, [])

  if (isError) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">
          {error?.message ?? 'Something went wrong while preparing your checkout.'}
        </p>
        <Button variant="secondary" asChild>
          <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
            Go to billing
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4">
      <Spinner size="5" />
      <p className="text-sm text-muted-foreground">Preparing your checkout...</p>
    </div>
  )
}
